import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';
import { authApi } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import styles from './AcceptInvitePage.module.css';

interface InvitationDetails {
  id: string;
  org_id: string;
  email: string;
  role: string;
  user_type: string;
  seat_type: string;
  status: string;
  expires_at: string;
  organization_name: string;
  invited_by_name: string;
}

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshOrg } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState('');
  const [signupMode, setSignupMode] = useState(true);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      const data = await apiClient.get<InvitationDetails>(`/invitations/by-token/${token}`);

      if (!data) {
        setError('Invitation not found');
        setLoading(false);
        return;
      }

      if (data.status === 'accepted') {
        setError('This invitation has already been accepted');
        setLoading(false);
        return;
      }

      if (data.status === 'expired' || new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired');
        setLoading(false);
        return;
      }

      if (data.status === 'revoked') {
        setError('This invitation has been revoked');
        setLoading(false);
        return;
      }

      setInvitation(data);
      setEmail(data.email);
      setLoading(false);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation details');
      setLoading(false);
    }
  };

  const acceptAndNavigate = async () => {
    if (token) {
      await apiClient.post('/invitations/accept', { token });
    }
    await refreshOrg();
    navigate('/app');
  };

  const handleExistingUserSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await authApi.signIn(email, password);
      await acceptAndNavigate();
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
      setSubmitting(false);
    }
  };

  const handleNewUserSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!displayName.trim()) {
      setError('Please enter your name');
      return;
    }

    setSubmitting(true);

    try {
      await authApi.signUp(email, password, displayName.trim());
      await acceptAndNavigate();
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.centeredCard}`}>
          <div className={styles.brand}>
            <div className={styles.logoIcon}>
              <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28" aria-hidden="true">
                <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h1 className={styles.appName}>AllocatIQ</h1>
              <p className={styles.tagline}>Resource Forecasting &amp; Utilization</p>
            </div>
          </div>
          <p className={styles.statusMessage}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.centeredCard}`}>
          <div className={styles.brand}>
            <div className={styles.logoIcon}>
              <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28" aria-hidden="true">
                <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h1 className={styles.appName}>AllocatIQ</h1>
              <p className={styles.tagline}>Resource Forecasting &amp; Utilization</p>
            </div>
          </div>
          <h2 className={styles.statusTitle}>Invalid Invitation</h2>
          <p className={styles.statusMessage}>{error}</p>
          <Link to="/login" className={styles.loginLink}>Go to Login</Link>
        </div>
      </div>
    );
  }

  const getRoleDescription = (userType: string) => {
    const descriptions: Record<string, string> = {
      owner: 'Full access to all features, billing, and organization management',
      admin: 'Full access to data and settings, can manage users and teams',
      manager: 'Can manage projects, resources, and team members within departments',
      contributor: 'Can create and edit own projects, resources, and allocations',
      viewer: 'Read-only access to view data and reports',
    };
    return descriptions[userType] || 'Team member';
  };

  const formatExpiry = (expiresAt: string) =>
    new Date(expiresAt).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logoIcon}>
            <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28" aria-hidden="true">
              <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h1 className={styles.appName}>AllocatIQ</h1>
            <p className={styles.tagline}>Resource Forecasting &amp; Utilization</p>
          </div>
        </div>

        <h2 className={styles.heading}>Team Invitation</h2>
        <p className={styles.intro}>
          <strong>{invitation?.invited_by_name}</strong> invited you to join{' '}
          <strong>{invitation?.organization_name}</strong>
        </p>

        <div className={styles.roleCard}>
          <p className={styles.roleLabel}>Your Role</p>
          <p className={styles.roleName}>
            {invitation?.user_type
              ? invitation.user_type.charAt(0).toUpperCase() + invitation.user_type.slice(1)
              : ''}
          </p>
          <p className={styles.roleDescription}>
            {invitation && getRoleDescription(invitation.user_type)}
          </p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {!signupMode ? (
          <form onSubmit={handleExistingUserSignIn} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="invite-email">Email</label>
              <input
                id="invite-email"
                type="email"
                value={email}
                disabled
                style={{ background: '#faf9f8', cursor: 'not-allowed' }}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="invite-password">Password</label>
              <input
                id="invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={submitting}
                required
              />
            </div>

            <button type="submit" className={styles.submitButton} disabled={submitting}>
              {submitting ? 'Signing In...' : 'Sign In & Accept Invitation'}
            </button>

            <button
              type="button"
              className={styles.switchMode}
              onClick={() => { setSignupMode(true); setError(''); }}
            >
              Don't have an account? Create one
            </button>
          </form>
        ) : (
          <form onSubmit={handleNewUserSignUp} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="invite-signup-email">Email</label>
              <input
                id="invite-signup-email"
                type="email"
                value={email}
                disabled
                style={{ background: '#faf9f8', cursor: 'not-allowed' }}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="invite-name">Your Name</label>
              <input
                id="invite-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your full name"
                disabled={submitting}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="invite-signup-password">Password</label>
              <input
                id="invite-signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min 8 characters)"
                disabled={submitting}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="invite-confirm-password">Confirm Password</label>
              <input
                id="invite-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={submitting}
                required
              />
            </div>

            <button type="submit" className={styles.submitButton} disabled={submitting}>
              {submitting ? 'Creating Account...' : 'Create Account & Accept Invitation'}
            </button>

            <button
              type="button"
              className={styles.switchMode}
              onClick={() => { setSignupMode(false); setError(''); }}
            >
              Already have an account? Sign in
            </button>
          </form>
        )}

        {invitation && (
          <div className={styles.expiration}>
            <strong>Note:</strong> This invitation expires on {formatExpiry(invitation.expires_at)}
          </div>
        )}
      </div>
    </div>
  );
}
